import TeamUser from 'domains/models/team_user';
import User from 'domains/models/user';
import { ViewerItem } from '../user';
import { schemaIntegrationAccountListItem } from 'presentations/api/schema/account';
import { z } from 'zod';
import { RoleEnum, StatusEnum } from './enum';

export const schemaTeamUserUpdateByAdminBody = z.object({
  role: RoleEnum,
});
export type TeamUserUpdateByAdminBody = z.infer<
  typeof schemaTeamUserUpdateByAdminBody
>;

export const schemaTeamUserInviteBody = z.object({
  emails: z.array(z.string()),
  role: RoleEnum.optional(),
});
export type TeamUserInviteBody = z.infer<typeof schemaTeamUserInviteBody>;

export const schemaTeamUserUpdateBody = z.object({
  defaultEventTypeColor: z.string().optional(),
  color: z.string().optional(),
  slug: z.string().optional(),
  profile: z.string().nullable().optional(),
});
export type TeamUserUpdateBody = z.infer<typeof schemaTeamUserUpdateBody>;

export const schemaUserNotificationUpdateBody = z.object({
  isMailDaily: z.boolean(),
  mailDailySendHour: z.number(),
});
export type UserNotificationUpdateBody = z.infer<
  typeof schemaUserNotificationUpdateBody
>;

export const schemaUserNotificationItem = z.object({
  isMailDaily: z.boolean(),
  mailDailySendHour: z.number(),
});
export type UserNotificationItem = z.infer<typeof schemaUserNotificationItem>;

export const schemaAdminTeamUserListItem = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  email: z.string().nullable(),
  role: RoleEnum,
  status: StatusEnum,
});
export type AdminTeamUserListItem = z.infer<typeof schemaAdminTeamUserListItem>;

export const schemaTeamUserListItem = z.object({
  id: z.number(),
  userId: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  email: z.string().nullable(),
  color: z.string(),
  slug: z.string(),
});
export type TeamUserListItem = z.infer<typeof schemaTeamUserListItem>;

export const schemaTeamUserItem = z.object({
  id: z.number(),
  userId: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  email: z.string().nullable(),
  color: z.string(),
  slug: z.string(),
});
export type TeamUserItem = z.infer<typeof schemaTeamUserItem>;

export const schemaSharedTeamUserListItem = z.object({
  userId: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  slug: z.string(),
  profile: z.string().nullable(),
});
export type SharedTeamUserListItem = z.infer<
  typeof schemaSharedTeamUserListItem
>;

export const schemaIntegrationTeamUserListItem = z.object({
  id: z.number(),
  userId: z.string(),
  name: z.string(),
  profileImage: z.string().nullable(),
  email: z.string(),
  accounts: z.array(schemaIntegrationAccountListItem),
});
export type IntegrationTeamUserListItem = z.infer<
  typeof schemaIntegrationTeamUserListItem
>;

export const sortToViewerTopListItems = (
  viewer: ViewerItem,
  teamUsers: TeamUserListItem[],
) => {
  for (let index = 0; index < teamUsers.length; index++) {
    const teamUser = teamUsers[index];
    if (teamUser.userId === viewer.uid) {
      const viewerTeamUser = teamUsers.splice(index, 1);
      teamUsers.unshift(viewerTeamUser[0]);
      return teamUsers;
    }
  }
  return teamUsers;
};

export const toListItem = (
  teamUser: TeamUser,
  user: User,
): TeamUserListItem => {
  return {
    id: Number(teamUser.id),
    userId: user.uid,
    name: user.name,
    profileImage: user.profileImage,
    email: user.email || null,
    color: teamUser.color,
    slug: teamUser.slug,
  };
};

export const toItem = (teamUser: TeamUser, user: User): TeamUserListItem => {
  return {
    id: Number(teamUser.id),
    userId: user.uid,
    name: user.name,
    profileImage: user.profileImage,
    email: user.email || null,
    color: teamUser.color,
    slug: teamUser.slug,
  };
};

export const toUserNotificationItem = (
  teamUser: TeamUser,
): UserNotificationItem => {
  return {
    isMailDaily: teamUser.isMailDaily,
    mailDailySendHour: teamUser.mailDailySendHour,
  };
};
